import { Component, OnInit, Renderer2 } from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { catchError, interval, Observable, of, Subscription } from 'rxjs';
import { CycleCountResponse } from 'src/app/wings-mobile/models/cycle-count/cycle-count-response';
import { CycleCountSaveRequest } from 'src/app/wings-mobile/models/cycle-count/cycle-count-save-request';
import { CycleCountZoneResponse } from 'src/app/wings-mobile/models/cycle-count/cycle-count-zone-response';
import { AuditCycleCount } from 'src/app/wings-mobile/services/auditCycleCount/auditCycleCount.service';
import { MessageBeep } from 'src/app/wings-mobile/services/messageBeep/messageBeep.service';
import { Globals } from 'src/app/wings-mobile/shared/globals/globals';
import { CycleCountTypeEnum } from '../audit/audit.component';
import { NgIf, NgStyle, AsyncPipe, UpperCasePipe } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonDirective } from 'primeng/button';

@Component({
    selector: 'cycle-counting',
    templateUrl: './cycle-count.component.html',
    styleUrls: ['./cycle-count.component.scss'],
    imports: [NgIf, NgStyle, SelectModule, FormsModule, InputTextModule, ButtonDirective, AsyncPipe, UpperCasePipe, TranslatePipe]
})
export class CycleCountComponent implements OnInit {

	cycleCountingResponse: CycleCountResponse[];

	quantityPartsSaved: CycleCountResponse[] = [];

	saveIsOk: boolean = true;

	countType = CycleCountTypeEnum;

	subscription: Subscription;

	returnedZones$: Observable<CycleCountZoneResponse[]>;
	returnedZonesError$: Observable<CycleCountZoneResponse[]>;

	loading: boolean = true;
	skipButtonDisable: boolean = true;

	quantityValue: number;

	itemScreen = 1;
	itemList = 0;

	selectedZone: CycleCountZoneResponse;
	currentZoneName: string;

	locationValue: string;
	enableQuantity: boolean;

	constructor(
		private auditCycleCount: AuditCycleCount,
		private beep: MessageBeep,
		public translate: TranslateService,
		public globals: Globals,
		private renderer: Renderer2
	) {
		this.renderer = renderer;
		this.globals = globals;
		this.auditCycleCount = auditCycleCount;
		this.beep = beep;
		this.translate = translate;
	}

	ngOnInit(): void {
		this.loadZones();
		this.enableQuantity = true;
	}

	loadZones() {
		let newObservable = interval(10);
		this.subscription = newObservable.subscribe(() => {
			if (this.globals.user.warehouse.bkSubDivisionUidD && this.globals.user.warehouse.warehouseUidK) {
				this.subscription.unsubscribe();
				this.returnedZones$ = this.auditCycleCount.getZonesWithPartsToBeCounted(['C', 'R']).pipe(
					catchError(error => {
						this.beep.showAlertCustom('modal.error', this.translate.instant('modal.infoRequestNotValid'));
						return of();
					})
				);
			}
		})
	}

	checkDropdownValid() {
		this.quantityValue = null;
		this.saveIsOk = true;
		this.itemScreen = 1;
		this.itemList = 0;
		this.locationValue = '';
		if (this.selectedZone && this.selectedZone !== undefined) {
			this.currentZoneName = undefined;
			this.auditCycleCount.getPartToCount(this.selectedZone.zoneC, ['C', 'R'])
				.subscribe({
					next: res => {
						if (res.length !== 0) {
							this.cycleCountingResponse = res;
							this.skipButtonDisable = false;
							this.cycleCountingResponse.forEach(item => {
								if (item.description === undefined || item.description === null) {
									item.description = "";
								}
							})
							this.currentZoneName = this.selectedZone.zoneC;
						} else {
							this.resetOnErrorLabelCycleCount(this.translate.instant('modal.noPiecesToCounted'))
						}
					},
					error: error => {
						if (typeof error.error !== 'string') {
							this.resetOnErrorLabelCycleCount(this.translate.instant('modal.connectionFailed'))
						} else {
							this.resetOnErrorLabelCycleCount(this.translate.instant(error.error))
						}
					}
				})
		}
	}

	resetOnErrorLabelCycleCount(errorMsg: string) {
		this.screenReset();
		this.beep.showAlertCustom('modal.error', errorMsg);
	}

	screenReset() {
		this.skipButtonDisable = true;
		this.cycleCountingResponse = null;
		this.quantityValue = null;
		this.selectedZone = undefined;
	}

	skipCountingPart() {
		if (this.itemScreen <= this.cycleCountingResponse.length) {
			this.itemScreen++;
			this.itemList++;
			this.quantityValue = null;
			this.invalidQuantity();
		}
		if (this.itemScreen > this.cycleCountingResponse.length) {
			this.cycleCountingResponse = this.cycleCountingResponse.filter((part) => {
				return this.quantityPartsSaved.indexOf(part) == -1
			});
			this.itemScreen = 1;
			this.itemList = 0;
			if (this.cycleCountingResponse.length === 0) {
				this.screenReset();
				this.validQuantity();
				this.loadZones();
			}
		}
		this.locationValue = '';
	}

	saveRequestGenerator(): CycleCountSaveRequest {
		let request: CycleCountSaveRequest = new CycleCountSaveRequest();
		request.cycleCountHistoryUidK = this.cycleCountingResponse[this.itemList].cycleCountHistoryUid;
		request.count = this.quantityValue;
		request.countOperatorC = this.globals.user.userName;
		request.cycleCountTypeC = "C";
		request.partCodeGspnPreffix = this.cycleCountingResponse[this.itemList].partCodeGspnPreffix;
		request.partCodeGspnBase = this.cycleCountingResponse[this.itemList].partCodeGspnBase;
		request.partCodeGspnSuffix = this.cycleCountingResponse[this.itemList].partCodeGspnSuffix;

		request.partXWarehouseUidK = this.cycleCountingResponse[this.itemList].partXWarehouseUidK;

		return request;
	}

	saveCountingPart() {
		this.auditCycleCount.save(this.saveRequestGenerator()).subscribe({
			next: res => {
				this.quantityPartsSaved.push(this.cycleCountingResponse[this.itemList]);
				this.skipCountingPart();
				this.locationValue = '';
				this.saveIsOk = true;
			},
			error: error => {
				if (typeof error.error !== 'string') {
					this.beep.showAlertCustom('modal.error', this.translate.instant('modal.connectionFailed'));
				} else {
					this.beep.showAlertCustom('modal.error', this.translate.instant(error.error));
				}
			}
		})
	}

	validQuantity() {
		if (this.quantityValue || this.quantityValue === 0) {
			this.saveIsOk = false;
		} else {
			this.saveIsOk = true;
		}
	}

	invalidQuantity() {
		this.saveIsOk = true;
	}

	invalidLocation() {
		this.enableQuantity = true;
		this.saveIsOk = true;
		this.quantityValue = null;
	}

	checkLocation() {
		this.locationValue = this.locationValue.toUpperCase();
		this.chackNameLocation();
		if (this.locationValue === this.cycleCountingResponse[this.itemList].location || this.locationValue === '999999') {
			this.enableQuantity = false;
			this.renderer.selectRootElement(`#label_quantity`).focus()
		} else {
			this.beep.showAlertCustom('modal.error', 'modal.stageNotFound');
			this.locationValue = '';
			this.quantityValue = null;
		}
	}

	chackNameLocation() {
		const inputFields = [
			{ value: null }
		]
		inputFields[0].value = this.locationValue;
		inputFields[0].value = inputFields[0].value.replaceAll(' ', '');
		inputFields[0].value = inputFields[0].value.replaceAll('*', '');
		this.locationValue = inputFields[0].value;
	}

}

